import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Avatar,
  alpha
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon, 
  Download as DownloadIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Paleta profesional verde-azul
const COLORS = {
  gradient1: '#00b894',
  gradient2: '#0984e3',
  primary: '#0984e3',
  success: '#00b894',
  warning: '#eb9800ff',
  error: '#d63031',
  purple: '#6c5ce7',
  dark: '#2d3436'
};

const DIFFICULTY_COLORS = {
  'basico': COLORS.success,
  'básico': COLORS.success,
  'intermedio': COLORS.warning,
  'avanzado': COLORS.error
};

const TestHistory = ({ data, isCourseView = false, userData = null, showExportButtons = false }) => {
  const [selectedCourse, setSelectedCourse] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage] = React.useState(5);

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Fecha no válida';
    }
  };

  const attempts = React.useMemo(() => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.attempts)) return data.attempts;
    return [];
  }, [data]);

  const filteredAttempts = useMemo(() => {
    return attempts.filter(attempt => 
      !selectedCourse || attempt.course_title === selectedCourse
    );
  }, [attempts, selectedCourse]);

  const stats = React.useMemo(() => {
    if (attempts.length === 0) {
      return { total: 0, passed: 0, failed: 0, avgScore: 0, passRate: 0 };
    }
    const total = attempts.length;
    const passed = attempts.filter(a => a.passed).length;
    const avgScore = attempts.reduce((acc, curr) => acc + (curr.score || 0), 0) / total;
    
    return {
      total,
      passed,
      failed: total - passed,
      avgScore,
      passRate: (passed / total) * 100
    };
  }, [attempts]);

  const difficultyStats = React.useMemo(() => {
    const stats = {
      basico: { total: 0, correct: 0 },
      intermedio: { total: 0, correct: 0 },
      avanzado: { total: 0, correct: 0 }
    };

    attempts.forEach(attempt => {
      attempt.questions?.forEach((question, index) => {
        const difficulty = question.difficulty?.toLowerCase() || 'basico';
        const answer = attempt.answers?.[index];
        
        if (stats[difficulty]) {
          stats[difficulty].total++;
          if (answer?.isCorrect) {
            stats[difficulty].correct++;
          }
        }
      });
    });

    return Object.entries(stats)
      .filter(([_, stats]) => stats.total > 0)
      .map(([difficulty, stats]) => ({
        difficulty,
        accuracy: (stats.correct / stats.total) * 100,
        total: stats.total,
        correct: stats.correct
      }));
  }, [attempts]);

  const exportToExcel = () => {
    if (!userData) return;
  
    const wb = XLSX.utils.book_new();
    const currentDate = new Date().toLocaleDateString('es-ES');
  
    const data = [
      ['INFORME DE USUARIO'],
      [''],
      ['Información del Usuario'],
      ['Nombre completo:', `${userData.nombre} ${userData.apellido}`],
      ['Usuario:', userData.username],
      ['Fecha del informe:', currentDate],
      ['Cursos completados:', userData.completed_courses || 0],
      ['Cursos en progreso:', userData.in_progress_courses || 0],
      ['Cursos inscritos:', userData.not_started_courses || 0],
      [''],
      ['Resumen de Intentos'],
      ['Total intentos:', stats.total],
      ['Aprobados:', stats.passed],
      ['Suspensos:', stats.failed],
      ['Tasa de aprobación:', `${stats.passRate.toFixed(1)}%`],
      [''],
      ['Rendimiento por Dificultad'],
      ['Dificultad', 'Porcentaje', 'Aciertos'],
    ];
  
    difficultyStats.forEach(stat => {
      data.push([
        stat.difficulty.charAt(0).toUpperCase() + stat.difficulty.slice(1),
        `${stat.accuracy.toFixed(1)}%`,
        `${stat.correct}/${stat.total}`
      ]);
    });
  
    data.push(
      [''],
      ['Historial Detallado de Intentos y Respuestas'],
      [''],
      ['ID Intento', 'Fecha', 'Curso', 'Pregunta', 'Dificultad', 'Respuesta', 'Estado', 'Puntuación']
    );
  
    attempts.forEach(attempt => {
      attempt.questions?.forEach((question, qIndex) => {
        data.push([
          attempt.id,
          formatDate(attempt.attempt_date),
          attempt.course_title,
          question.question,
          question.difficulty || 'básico',
          attempt.answers?.[qIndex]?.userAnswer,
          attempt.answers?.[qIndex]?.isCorrect ? 'Correcta' : 'Incorrecta',
          `${attempt.score}%`
        ]);
      });
    });
  
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = [
      { wch: 15 }, { wch: 20 }, { wch: 30 }, { wch: 50 },
      { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 15 }
    ];
  
    XLSX.utils.book_append_sheet(wb, ws, 'Informe Usuario');
    XLSX.writeFile(wb, `informe_usuario_${userData.username}_${currentDate.replace(/\//g, '-')}.xlsx`);
  };

  const exportToPDF = () => {
    if (!userData) return;
  
    const doc = new jsPDF();
    const currentDate = new Date().toLocaleDateString('es-ES');
    let yPos = 15;
  
    const addTitle = (text, y) => {
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(text, 14, y);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(12);
      return y + 10;
    };
  
    yPos = addTitle('INFORME DE USUARIO', yPos);
    yPos += 5;
    yPos = addTitle('Información del Usuario', yPos);
    doc.text([
      `Nombre completo: ${userData.nombre} ${userData.apellido}`,
      `Usuario: ${userData.username}`,
      `Fecha del informe: ${currentDate}`,
      `Cursos completados: ${userData.completed_courses || 0}`,
      `Cursos en progreso: ${userData.in_progress_courses || 0}`,
      `Cursos inscritos: ${userData.not_started_courses || 0}`
    ], 14, yPos);
    yPos += 40;
  
    yPos = addTitle('Rendimiento por Dificultad', yPos);
    doc.autoTable({
      startY: yPos,
      head: [['Dificultad', 'Porcentaje', 'Aciertos']],
      body: difficultyStats.map(stat => [
        stat.difficulty,
        `${stat.accuracy.toFixed(1)}%`,
        `${stat.correct}/${stat.total}`
      ])
    });
  
    doc.addPage();
    doc.setFontSize(14);
    doc.text('Historial Detallado de Intentos y Respuestas', 14, 15);
  
    const detailedData = attempts.flatMap(attempt => 
      attempt.questions?.map((question, qIndex) => [
        attempt.id,
        formatDate(attempt.attempt_date),
        attempt.course_title,
        question.question,
        question.difficulty || 'básico',
        attempt.answers?.[qIndex]?.userAnswer,
        attempt.answers?.[qIndex]?.isCorrect ? 'Correcta' : 'Incorrecta',
        `${attempt.score}%`
      ])
    );
  
    doc.autoTable({
      startY: 25,
      head: [['ID', 'Fecha', 'Curso', 'Pregunta', 'Dificultad', 'Respuesta', 'Estado', 'Puntuación']],
      body: detailedData
    });
  
    doc.save(`informe_tests_${userData.username}_${currentDate.replace(/\//g, '-')}.pdf`);
  };

  if (attempts.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          textAlign: 'center',
          borderRadius: 3,
          border: '2px dashed #e0e0e0'
        }}
      >
        <HistoryIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
        <Typography variant="h6" sx={{ color: '#636e72' }}>
          No hay datos de test disponibles
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={3}>
        {/* Botones de Exportación */}
        {showExportButtons && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', my: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ExcelIcon />}
                onClick={exportToExcel}
                sx={{
                  borderColor: COLORS.success,
                  color: COLORS.success,
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': { borderColor: COLORS.success, bgcolor: alpha(COLORS.success, 0.08) }
                }}
              >
                Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={<PdfIcon />}
                onClick={exportToPDF}
                sx={{
                  borderColor: COLORS.error,
                  color: COLORS.error,
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': { borderColor: COLORS.error, bgcolor: alpha(COLORS.error, 0.08) }
                }}
              >
                PDF
              </Button>
            </Box>
          </Grid>
        )}

        {/* Historial Detallado */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${COLORS.purple} 0%, ${COLORS.primary} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <HistoryIcon sx={{ color: 'white', fontSize: 20 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>
              Historial Detallado
            </Typography>
          </Box>

          {/* Filtro */}
          <Paper 
            elevation={0}
            sx={{ p: 2, mb: 3, borderRadius: 2, border: '2px solid #e0e0e0' }}
          >
            <FormControl fullWidth size="small">
              <InputLabel>Filtrar por curso</InputLabel>
              <Select
                value={selectedCourse}
                onChange={(e) => { setSelectedCourse(e.target.value); setPage(0); }}
                label="Filtrar por curso"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">Todos los cursos</MenuItem>
                {[...new Set(attempts.map(a => a.course_title))].map((course) => (
                  <MenuItem key={course} value={course}>{course}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>

          {/* Lista de intentos */}
          {filteredAttempts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((attempt, index) => (
            <Paper 
              key={index} 
              elevation={0}
              sx={{ 
                mb: 2,
                borderRadius: 3,
                border: '2px solid #e0e0e0',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: alpha(COLORS.primary, 0.3),
                  boxShadow: `0 4px 12px ${alpha(COLORS.primary, 0.1)}`
                }
              }}
            >
              <Box 
                sx={{ 
                  p: 2.5,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                  gap: 2,
                  bgcolor: alpha(COLORS.primary, 0.02)
                }}
              >
                {/* Curso */}
                <Box>
                  <Typography variant="caption" sx={{ color: '#9e9e9e', fontWeight: 600, textTransform: 'uppercase' }}>
                    Curso
                  </Typography>
                  <Typography sx={{ fontWeight: 600, color: COLORS.dark, mt: 0.5 }}>
                    {attempt.course_title}
                  </Typography>
                </Box>

                {/* Fecha */}
                <Box>
                  <Typography variant="caption" sx={{ color: '#9e9e9e', fontWeight: 600, textTransform: 'uppercase' }}>
                    Fecha
                  </Typography>
                  <Typography sx={{ fontWeight: 500, color: COLORS.dark, mt: 0.5 }}>
                    {attempt.attempt_date.split('T')[0].split('-').reverse().join('/')} {" "}
                    {attempt.attempt_date.split('T')[1]?.substring(0, 5)}
                  </Typography>
                </Box>

                {/* Puntuación */}
                <Box>
                  <Typography variant="caption" sx={{ color: '#9e9e9e', fontWeight: 600, textTransform: 'uppercase' }}>
                    Puntuación
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: attempt.passed ? COLORS.success : COLORS.error, mt: 0.5, fontSize: '1.1rem' }}>
                    {parseFloat(attempt.score).toFixed(0)}%
                  </Typography>
                </Box>

                {/* Estado */}
                <Box>
                  <Typography variant="caption" sx={{ color: '#9e9e9e', fontWeight: 600, textTransform: 'uppercase' }}>
                    Estado
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      icon={attempt.passed ? <CheckCircleIcon /> : <CancelIcon />}
                      label={attempt.passed ? 'Aprobado' : 'Suspenso'}
                      size="small"
                      sx={{
                        bgcolor: alpha(attempt.passed ? COLORS.success : COLORS.error, 0.15),
                        color: attempt.passed ? COLORS.success : COLORS.error,
                        fontWeight: 600,
                        '& .MuiChip-icon': { color: attempt.passed ? COLORS.success : COLORS.error }
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Acordeón de respuestas */}
              <Accordion 
                disableGutters 
                elevation={0}
                sx={{ '&:before': { display: 'none' } }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon sx={{ color: COLORS.primary }} />}
                  sx={{ 
                    borderTop: '1px solid #e0e0e0',
                    '&:hover': { bgcolor: alpha(COLORS.primary, 0.04) }
                  }}
                >
                  <Typography sx={{ fontWeight: 600, color: COLORS.primary }}>
                    Ver respuestas ({attempt.questions?.length || 0})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: alpha(COLORS.primary, 0.05) }}>
                          <TableCell sx={{ fontWeight: 700 }}>Pregunta</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Dificultad</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Respuesta</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {attempt.questions?.map((question, qIndex) => {
                          const diffColor = DIFFICULTY_COLORS[question.difficulty?.toLowerCase()] || COLORS.success;
                          const isCorrect = attempt.answers?.[qIndex]?.isCorrect;
                          return (
                            <TableRow key={qIndex}>
                              <TableCell sx={{ maxWidth: 300 }}>{question.question}</TableCell>
                              <TableCell>
                                <Chip
                                  label={question.difficulty || 'básico'}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(diffColor, 0.15),
                                    color: diffColor,
                                    fontWeight: 600
                                  }}
                                />
                              </TableCell>
                              <TableCell>{attempt.answers?.[qIndex]?.userAnswer}</TableCell>
                              <TableCell>
                                <Chip
                                  icon={isCorrect ? <CheckCircleIcon /> : <CancelIcon />}
                                  label={isCorrect ? 'Correcta' : 'Incorrecta'}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(isCorrect ? COLORS.success : COLORS.error, 0.15),
                                    color: isCorrect ? COLORS.success : COLORS.error,
                                    fontWeight: 600,
                                    '& .MuiChip-icon': { color: isCorrect ? COLORS.success : COLORS.error, fontSize: 16 }
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Paper>
          ))}

          {/* Paginación */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={Math.ceil(filteredAttempts.length / rowsPerPage)}
              page={page + 1}
              onChange={(event, newPage) => setPage(newPage - 1)}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontWeight: 600,
                  '&.Mui-selected': {
                    background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                    color: 'white'
                  }
                }
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TestHistory;